import * as B from '@blueprintjs/core';
import * as Sentry from '@sentry/react';
import * as turf from '@turf/turf';
import geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import {ToastNotification} from 'app/modules/Notification';
import {api} from 'app/modules/Remote';
import type {ApiImageryBudget} from 'app/modules/Remote/api';
import {ApiFeature, ApiOrderableScene} from 'app/modules/Remote/Feature';
import {ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiDollarUnitUsage, ApiImageryContract} from 'app/modules/Remote/Project';
import {useImageryPrices} from 'app/providers/ImageryPricesProvider';
import {recordEvent} from 'app/tools/Analytics';
import {ApiResponseError} from 'app/utils/apiUtils';
import * as CONSTANTS from 'app/utils/constants';
import * as C from 'app/utils/constants';
import * as conversionUtils from 'app/utils/conversionUtils';
import {calculateCost, formatCost} from 'app/utils/costUtils';
import * as featureUtils from 'app/utils/featureUtils';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import {calculateBillableUTMAreaInAcres} from 'app/utils/geoJsonUtils';
import {StatusMaybe} from 'app/utils/hookUtils';
import {
  CHLORIS_10_PACKAGE,
  CHLORIS_30_PACKAGE,
  CTREES_TREE_HEIGHT,
  LAYER_PACKAGES,
  PLANET_FOREST_DILIGENCE_PACKAGE,
  PLANET_FOREST_MONITORING_PACKAGE,
} from 'app/utils/layers';
import {getLayer, isLayerKeyRaw, parseLayerKey} from 'app/utils/layerUtils';
import * as userUtils from 'app/utils/userUtils';

import cs from './styles.module.scss';

/*
 * Helper function callers can use to actually order bulk imagery
 */

export async function orderBulkImagery(
  projectId: string,
  featureCollectionId: number,
  featureIds: number[],
  sourceId: string,
  sensingTime: string | null,
  expectedCostInCents: number,
  pushNotification: (p: ToastNotification) => void,
  sensingTimeMode?: 'all' | 'latest' | 'explicit'
) {
  try {
    await api.featureCollections
      .features(featureCollectionId)
      .orderImageryBulk(
        projectId,
        featureIds,
        sensingTime,
        sourceId,
        expectedCostInCents,
        sensingTimeMode
      );

    recordEvent('Placed a bulk order', {
      projectId,
      featureCollectionId,
      featureIds: featureIds.join(','),
      sourceId,
      // sensingTime is null for per-feature sources.
      // recordEvent can't take nulls,
      sensingTime: sensingTime ?? 'null',
    });

    pushNotification({
      message:
        'The requested data has been ordered. You will receive an email when processing is complete.',
      autoHideDuration: 5000,
      options: {intent: B.Intent.SUCCESS},
    });
  } catch (e) {
    const {error} = e as ApiResponseError;

    Sentry.captureException(error, {
      extra: {
        featureIds,
        sourceId,
        sensingTime,
        billableCost: expectedCostInCents,
      },
    });

    if (error.message.includes('Total cost mismatch')) {
      pushNotification({
        message:
          'Error ordering the requested data. The price of this data has changed. Please try again',
        options: {
          intent: B.Intent.DANGER,
        },
      });
    } else {
      pushNotification({
        message: 'Error ordering the requested data. Our team has been notified and we are on it!',
        options: {
          intent: B.Intent.DANGER,
        },
      });
    }

    return null;
  }
}

export type NteScope = 'contract' | 'organization';

/** The not-to-exceed cap an order counts against, and what's already been spent toward it. */
export interface NteSource {
  scope: NteScope;
  /** Null is no cap. */
  nteCents: number | null;
  spentCents: number;
}

/**
 * An organization on budgets has one cap across all its contracts, so its budget wins over the
 * contract's NTE even when the budget has no cap.
 */
export function nteSource(usage: ApiDollarUnitUsage, budget: ApiImageryBudget | null): NteSource;
export function nteSource(
  usage: ApiDollarUnitUsage | null,
  budget: ApiImageryBudget | null
): NteSource | null;
export function nteSource(
  usage: ApiDollarUnitUsage | null,
  budget: ApiImageryBudget | null
): NteSource | null {
  if (budget !== null) {
    return {scope: 'organization', nteCents: budget.cents, spentCents: budget.spentCents};
  }
  if (usage !== null) {
    return {scope: 'contract', nteCents: usage.nte_cents, spentCents: usage.purchased_cents};
  }
  return null;
}

/**
 * Whether an order's contract and budget have both loaded. The budget, when there is one, is the cap
 * the order counts against, so neither is shown on its own.
 */
export function purchaseInfoStatus(
  contract: StatusMaybe<unknown>,
  budget: {data: unknown; isLoadingError: boolean; isFetching: boolean}
): StatusMaybe<unknown>['status'] {
  // A failed refetch keeps the budget it had, so it doesn't take down a purchase in progress; the
  // server checks the cap either way. A retry of a failed load shows as loading.
  if (contract.status === 'error' || (budget.isLoadingError && !budget.isFetching)) {
    return 'error';
  }
  return contract.status === 'some' && budget.data !== undefined ? 'some' : 'unknown';
}

/**
 * Why an order over its cap can't be placed. Renders nothing for an order that fits. A preview
 * only warns: the scene can still go in the cart, and the cart's checkout is what the cap blocks.
 */
export const NteExceededNotice: React.FunctionComponent<{
  /** Null without a contract or a budget, so nothing caps the order. */
  nte: NteSource | null;
  orderCents: number;
  layerKey: string;
  isPreview?: boolean;
}> = ({nte, orderCents, layerKey, isPreview = false}) => {
  if (nte === null || nte.nteCents === null || nte.spentCents + orderCents <= nte.nteCents) {
    return null;
  }
  const {nteCents, spentCents} = nte;

  const orderableSceneType = layerKey in LAYER_PACKAGES ? 'package' : 'image';
  const capHolder = nte.scope === 'organization' ? 'your organization' : 'this portfolio';
  const remainingCents = nteCents - spentCents;

  if (isPreview) {
    return (
      <>
        <div>
          {remainingCents > 0 ? (
            <>
              This {orderableSceneType} costs <strong>{formatCost(orderCents)}</strong>, but only{' '}
              <strong>{formatCost(remainingCents)}</strong>
              {` remains before you hit ${capHolder}'s spending limit.`}
            </>
          ) : (
            `There are no funds remaining for ${capHolder}.`
          )}{' '}
          You can add this {orderableSceneType} to your cart, but you won’t be able to check out
          until more funds are available.
        </div>

        <div>Please have an account administrator contact us for options.</div>
      </>
    );
  } else if (remainingCents > 0) {
    return (
      <>
        <div>
          Ordering this {orderableSceneType} costs <strong>{formatCost(orderCents)}</strong>, but
          you cannot order it because there is only <strong>{formatCost(remainingCents)}</strong>
          {` remaining before you hit ${capHolder}'s spending limit.`}
        </div>

        <div>Please have an account administrator contact us for options.</div>
      </>
    );
  } else {
    return (
      <>
        <div>
          Ordering this {orderableSceneType} costs <strong>{formatCost(orderCents)}</strong>
          {`, but you cannot order it because there are no funds remaining for ${capHolder}.`}
        </div>

        <p>Please have an account administrator contact us for options.</p>
      </>
    );
  }
};

export const DollarUnitContractDetails: React.FunctionComponent<{
  /** Null without a contract, whose prepaid this would otherwise describe. */
  usage: ApiDollarUnitUsage | null;
  /** Null without a contract or a budget, so nothing caps the order. */
  nte: NteSource | null;
  acreage: number;
  layerKey: string;
  pricePerAcre: number;
  orderCents?: number; // If provided, this will override the cost calculation of pricePerAcre * acreage
}> = ({usage, nte, acreage, layerKey, pricePerAcre, orderCents}) => {
  const orderableSceneType = layerKey in LAYER_PACKAGES ? 'package' : 'image';
  const nteCents = nte?.nteCents ?? null;
  const usedCents = nte?.spentCents ?? 0;
  const capHolder = nte?.scope === 'organization' ? 'your organization' : 'this portfolio';
  const currentOrderCents = orderCents ?? calculateCost(acreage, pricePerAcre);

  // Without a contract or a cap there's no prepaid or limit to describe.
  if (nteCents === null && usage === null) {
    return null;
  }

  // Prepaid credit belongs to the contract, so it's drawn down by the contract's own purchases
  // even when an organization budget is what sets the cap.
  const remainingPrepaidCents = usage === null ? 0 : usage.prepaid_cents - usage.purchased_cents;

  if (nteCents === null) {
    if (remainingPrepaidCents <= 0) {
      return (
        <>
          By clicking “Order," you agree to this charge. Orders cannot be refunded after purchase.
        </>
      );
    } else if (remainingPrepaidCents >= currentOrderCents) {
      return (
        <>
          Ordering this {orderableSceneType} will use{' '}
          <strong>{formatCost(currentOrderCents)}</strong> of the remaining prepaid{' '}
          <strong>{formatCost(remainingPrepaidCents)}</strong> for this portfolio. Please note that
          orders cannot be refunded after purchase.
        </>
      );
    } else {
      return (
        <>
          By clicking “Order,” you agree to use the remaining prepaid{' '}
          <strong>{formatCost(remainingPrepaidCents)}</strong> for this portfolio and a charge of{' '}
          <strong>{formatCost(Math.max(currentOrderCents - remainingPrepaidCents))}</strong> on your
          next invoice. Orders cannot be refunded after purchase.
        </>
      );
    }
  } else if (usedCents + currentOrderCents <= nteCents) {
    const remainingCents = nteCents - usedCents;

    return (
      <>
        Ordering this {orderableSceneType} will use <strong>{formatCost(currentOrderCents)}</strong>{' '}
        of the remaining <strong>{formatCost(remainingCents)}</strong>
        {` before you hit ${capHolder}'s spending limit. `}
        {remainingPrepaidCents <= 0 ? null : remainingPrepaidCents >= currentOrderCents ? (
          <>
            It will be paid from the remaining prepaid{' '}
            <strong>{formatCost(remainingPrepaidCents)}</strong> for this portfolio.{' '}
          </>
        ) : (
          <>
            By clicking “Order,” you agree to use the remaining prepaid{' '}
            <strong>{formatCost(remainingPrepaidCents)}</strong> for this portfolio and a charge of{' '}
            <strong>{formatCost(currentOrderCents - remainingPrepaidCents)}</strong> on your next
            invoice.{' '}
          </>
        )}
        Orders cannot be refunded after purchase.
      </>
    );
  } else {
    return <NteExceededNotice nte={nte} orderCents={currentOrderCents} layerKey={layerKey} />;
  }
};

export function getBillableAcres(
  feature: I.ImmutableOf<ApiFeature>,
  orderableScene: I.ImmutableOf<ApiOrderableScene>,
  subsetFeature: geojson.Feature<geojson.Polygon | geojson.MultiPolygon> | null
) {
  const orderGeometryAcres = getOrderGeometryAcres(feature, orderableScene, subsetFeature);
  const minimumAcres =
    C.SOURCE_DETAILS_BY_ID[orderableScene.get('sourceId')]?.minimum_order_acres || 0;
  const billableAcres = Math.max(orderGeometryAcres, minimumAcres);
  return {orderGeometryAcres, billableAcres};
}
export function getOrderGeometryAcres(
  feature: I.ImmutableOf<ApiFeature>,
  orderableScene: I.ImmutableOf<ApiOrderableScene>,
  subsetFeature: geojson.Feature<geojson.Polygon | geojson.MultiPolygon> | null
) {
  // Calculate billableAcres if we are ordering partial imagery or if
  // partial imagery has already been ordered, otherwise just use the information
  // on the feature
  if (!orderableScene.get('orderedGeometry') && !subsetFeature) {
    const intersectingGeometry = featureUtils.getSceneAndFeatureIntersectionGeometry(
      orderableScene,
      feature
    );

    // Calculate area using UTM projection to match backend
    const areaInAcres = calculateBillableUTMAreaInAcres(intersectingGeometry);

    // Apply the same rounding logic as backend: round down and ensure minimum of 1 acre
    return Math.max(1, Math.floor(areaInAcres));
  }

  // Rounds to decimal to 6 places
  const roundDecimalPlace = (position: geojson.Position) =>
    position.map((num) => Math.floor(num * 1000000) / 1000000);

  // Start with geometry of the feature
  let billableGeometry: geojson.Polygon | geojson.MultiPolygon | undefined = feature
    .get('geometry')
    .toJS();

  // Round all coordinates to 6 decimal places to avoid floating point math errors in turf
  // calculations
  billableGeometry = geoJsonUtils.applyToCoordinates(billableGeometry, roundDecimalPlace);

  try {
    // If a subset is being ordered take the intersection of the subset geometry and the feature
    if (subsetFeature && feature) {
      const subsetFeatureGeometry = subsetFeature.geometry;
      billableGeometry =
        billableGeometry &&
        geoJsonUtils.intersectGeometries(subsetFeatureGeometry, billableGeometry)?.geometry;
    }

    // If some geometry has already been ordered for a given scene, remove that area from
    // the billableGeometry
    if (orderableScene.get('orderedGeometry')) {
      let alreadyOrderedGeometry = orderableScene.get('orderedGeometry')?.toJS() as
        | geojson.Polygon
        | geojson.MultiPolygon
        | undefined;

      // Round all coordinates to 6 decimal places to avoid floating point math errors in turf
      // calculations
      alreadyOrderedGeometry = geoJsonUtils.applyToCoordinates(
        alreadyOrderedGeometry,
        roundDecimalPlace
      );

      billableGeometry =
        billableGeometry &&
        alreadyOrderedGeometry &&
        turf.difference(
          turf.featureCollection([
            turf.feature(billableGeometry),
            turf.feature(alreadyOrderedGeometry),
          ])
        )?.geometry;
    }

    // Lastly take the intersection of the previous steps with the geometry of the scene
    const sceneGeometry = orderableScene.get('sceneGeometry').toJS() as
      | geojson.Polygon
      | geojson.MultiPolygon;

    // Calculate area using our UTM-based approach to match backend
    let billableAcres = 0;
    if (billableGeometry) {
      // Get the intersection between billable geometry and scene geometry
      const intersectionFeature = geoJsonUtils.intersectGeometries(billableGeometry, sceneGeometry);

      if (intersectionFeature) {
        // Use a UTM projection-based area calculation to match backend
        billableAcres = calculateBillableUTMAreaInAcres(intersectionFeature.geometry);
      }
    }

    return billableAcres;
  } catch (e) {
    // Calculating the difference/intersection for features that have very small differences may fail.
    // This should be handled by the API and treated as a fully ordered feature, but if not we want
    // to catch it.
    Sentry.captureException(e, {
      extra: {
        featureId: feature.get('id'),
        orderableSceneSource: orderableScene.get('sourceId'),
        orderableSceneSensingTime: orderableScene.get('sensingTime'),
        subsetGeometry: JSON.stringify(subsetFeature?.geometry ?? null),
      },
    });
    console.error(e);
    return -1;
  }
}

export function formatAcres(acreage: number): string {
  return conversionUtils.numberWithCommas(acreage.toString());
}

export const ORDERABLE_SCENE_ERRORS = {
  ORDER_STILL_PROCESSING: 'Order is still processing',
  UNABLE_TO_ORDER: 'Unable to order for this property',
};

export function validateOrderableScene(orderableScene: ApiOrderableScene): {error?: string} {
  if (orderableScene.isOrdered && !orderableScene.orderProcessed) {
    return {error: ORDERABLE_SCENE_ERRORS.ORDER_STILL_PROCESSING};
  }
  if (!orderableScene.canBeOrdered) {
    return {error: ORDERABLE_SCENE_ERRORS.UNABLE_TO_ORDER};
  }
  return {};
}

export interface OrderLicense {
  name: string;
  url: string;
}

const CHLORIS_LICENSE: OrderLicense = {
  name: 'Chloris End User License Agreement',
  url: 'https://cdn.prod.website-files.com/6197d5aab3f545ea3c1c5438/671b9d5827701b056d11fa69_Chloris%20Geospatial%20AGB%20EULA.pdf',
};

const CTREES_LICENSE: OrderLicense = {
  name: 'CTrees End User License Agreement',
  url: 'https://drive.google.com/file/d/1TCAw720DaTvFxCUJsjQtV7AN6k-wlV35/view',
};

/** The provider's license that ordering from a source agrees to. */
export const ORDER_LICENSE_BY_SOURCE_ID: Partial<Record<string, OrderLicense>> = {
  'CHLORIS-30': CHLORIS_LICENSE,
  'CHLORIS-10': CHLORIS_LICENSE,
  'CTREES-TREE-HEIGHT': CTREES_LICENSE,
};

export const getPackageOrderText = (
  layerKey: string,
  isBulkOrder: boolean = false,
  sensingTimeMode: 'all' | 'latest' | 'explicit' = 'explicit'
) => {
  const PACKAGE_ORDER_TEXT: {[layerKey: string]: React.ReactNode} = {
    [PLANET_FOREST_DILIGENCE_PACKAGE]: (
      <>
        <p>
          The Planet Forest Carbon Diligence Package includes the following three layers. If this is
          the first time you're purchasing data for{' '}
          {isBulkOrder ? 'a property in this selection' : 'this property'}, the full archive of
          annual data back to 2013 will be included at no extra cost.
        </p>
        <ol>
          {LAYER_PACKAGES[PLANET_FOREST_DILIGENCE_PACKAGE].filter(
            (layerKey) => !isLayerKeyRaw(layerKey)
          ).map((layerKey) => (
            <li key={layerKey}>{getLayer(layerKey).display}</li>
          ))}
        </ol>
        <p>
          See our{' '}
          <a
            href="https://lens.tech/support/article/157-planet-forest-carbon-diligence"
            target="_blank"
            rel="noopener noreferrer"
          >
            support doc here
          </a>{' '}
          for more details.
        </p>
      </>
    ),
    [PLANET_FOREST_MONITORING_PACKAGE]: (
      <>
        <p>
          The Planet Forest Carbon Monitoring Package includes the following three layers at 3m
          resolution. If this is the first time you're purchasing data for{' '}
          {isBulkOrder ? 'a property in this selection' : 'this property'}, the archive of quarterly
          data back to 2021 will be included at no extra cost.
        </p>
        <ol>
          {LAYER_PACKAGES[PLANET_FOREST_MONITORING_PACKAGE].filter(
            (layerKey) => !isLayerKeyRaw(layerKey)
          ).map((layerKey) => (
            <li key={layerKey}>{getLayer(layerKey).display}</li>
          ))}
        </ol>
        <p>
          See our{' '}
          <a
            href="https://lens.tech/support/article/157-planet-forest-carbon-diligence#3meter"
            target="_blank"
            rel="noopener noreferrer"
          >
            support doc here
          </a>{' '}
          for more details.
        </p>
      </>
    ),
    [CHLORIS_30_PACKAGE]: (
      <>
        <p>
          The Chloris 30m package includes data on the following layers. If this is your first
          purchase for {isBulkOrder ? 'a property in this selection' : 'this property'}, the full
          archive of annual data back to 2000 will be included at no extra cost.
        </p>
        <ol>
          {LAYER_PACKAGES[CHLORIS_30_PACKAGE].filter((layerKey) => !isLayerKeyRaw(layerKey)).map(
            (layerKey) => (
              <li key={layerKey}>{getLayer(layerKey).display}</li>
            )
          )}
        </ol>
        <p>
          Chloris data is not pre-processed and may take up to 5 business days to process and
          deliver into your account. By placing this order, you agree to comply with the{' '}
          <a href={CHLORIS_LICENSE.url} target="_blank" rel="noopener noreferrer">
            Chloris End User License Agreement linked here.
          </a>
        </p>
        <p>
          See our{' '}
          <a
            href="https://lens.tech/support/article/170-chloris-forest-carbon"
            target="_blank"
            rel="noopener noreferrer"
          >
            support doc
          </a>{' '}
          for more details.
        </p>
      </>
    ),
    [CHLORIS_10_PACKAGE]: (
      <>
        <p>
          The Chloris 10m package includes data on the following layers. If this is your first
          purchase for {isBulkOrder ? 'a property in this selection' : 'this property'}, the full
          archive of annual data back to 2017 will be included at no extra cost.
        </p>
        <ol>
          {LAYER_PACKAGES[CHLORIS_10_PACKAGE].filter((layerKey) => !isLayerKeyRaw(layerKey)).map(
            (layerKey) => (
              <li key={layerKey}>{getLayer(layerKey).display}</li>
            )
          )}
        </ol>
        <p>
          Chloris data is not pre-processed and may take up to 5 business days to process and
          deliver into your account. By placing this order, you agree to comply with the{' '}
          <a href={CHLORIS_LICENSE.url} target="_blank" rel="noopener noreferrer">
            Chloris End User License Agreement linked here.
          </a>
        </p>
        <p>
          See our{' '}
          <a
            href="https://lens.tech/support/article/170-chloris-forest-carbon"
            target="_blank"
            rel="noopener noreferrer"
          >
            support doc
          </a>{' '}
          for more details.
        </p>
      </>
    ),
    [CTREES_TREE_HEIGHT]: (
      <>
        <p>
          CTrees Tree Canopy Height provides 60cm spatial resolution tree canopy height for the
          contiguous United States.{' '}
          {isBulkOrder && sensingTimeMode === 'latest'
            ? 'The latest available annual scene for each property in this selection will be ordered at $0.10/acre per scene.'
            : isBulkOrder
              ? 'All available annual scenes for each property in this selection will be ordered at $0.10/acre per scene.'
              : 'All available annual scenes for this property will be ordered at $0.10/acre per scene.'}
        </p>
        <p>
          By placing this order, you agree to comply with the{' '}
          <a href={CTREES_LICENSE.url} target="_blank" rel="noopener noreferrer">
            CTrees End User License Agreement linked here.
          </a>
        </p>
        <p>
          See{' '}
          <a href="https://ctrees.org/" target="_blank" rel="noopener noreferrer">
            CTrees.org
          </a>{' '}
          for more details.
        </p>
      </>
    ),
  };
  return PACKAGE_ORDER_TEXT[layerKey];
};

export const ContractDetailsDescription: React.FC<{
  isDisabledSource: boolean;
  isProcessing?: boolean;
  sceneCoverageFailed: boolean;
  canPurchase: boolean;
  orderExceedsMaximumOrderAcres?: boolean;
  layerKey: string;
  sceneSensingTime?: string;
  orderGeometryAcres: number;
  billableAcres: number;
  /** Null for an org that pays at checkout, which bills no contract. */
  imageryContract: ApiImageryContract | null;
  imageryBudget: ApiImageryBudget | null;
  acresMessage: string;
  orderCents?: number; // For bulk orders, we use the value from the API response instead of calculating it on the frontend
  role: ApiOrganizationUser['role'];
  supportsCopyLink?: boolean; // Optional prop to indicate if the Copy Link button should be referenced for users who cannot order imagery. Defaults to true
  // A previewed scene is ordered from the cart, whose checkout says what the order agrees to, so
  // the preview only speaks up when the cap would block that checkout.
  isPreview?: boolean;
}> = ({
  isDisabledSource,
  isProcessing,
  sceneCoverageFailed,
  canPurchase,
  orderExceedsMaximumOrderAcres,
  layerKey,
  sceneSensingTime,
  orderGeometryAcres,
  billableAcres,
  imageryContract,
  imageryBudget,
  acresMessage,
  orderCents,
  role,
  supportsCopyLink = true,
  isPreview = false,
}) => {
  const {sourceId} = parseLayerKey(layerKey);
  const sourceDetails = CONSTANTS.SOURCE_DETAILS_BY_ID[sourceId];
  const {prices} = useImageryPrices();
  const scenePricePerAcre: number | null = prices[sourceId];
  const usage = imageryContract ? (imageryContract.usage as ApiDollarUnitUsage) : null;
  const nte = nteSource(usage, imageryBudget);

  if (isDisabledSource) {
    return (
      <B.Callout icon={null} intent={B.Intent.DANGER}>
        <p>
          We’re sorry, we’re temporarily unable to process{' '}
          <strong>
            {sourceDetails.operator} {sourceDetails.name}
          </strong>{' '}
          data.
        </p>

        <p>
          Please check back soon or contact us at <strong>lens@upstream.tech</strong> with any
          questions.
        </p>
      </B.Callout>
    );
  }
  if (isProcessing) {
    return <p>We’re processing your order and will email you when it's ready.</p>;
  }
  if (sceneCoverageFailed) {
    return (
      <B.NonIdealState
        iconMuted={false}
        className={cs.sceneCoverageFailed}
        icon="warning-sign"
        description={
          <>
            <p>There is a problem with the geometry that prevents this scene from being ordered.</p>
            <p>
              Our engineers have been notified and will investigate. If this problem persists,
              please contact <strong>lens@upstream.tech</strong> for support.
            </p>
          </>
        }
      />
    );
  }
  if (!userUtils.mayOrderImagery(role)) {
    return (
      <B.Callout icon={null} intent={B.Intent.WARNING}>
        <p>Only organization admins may order data.</p>
        {supportsCopyLink && (
          <p>Click the Copy Link button to get a sharable URL for this scene.</p>
        )}
      </B.Callout>
    );
  }
  if (!scenePricePerAcre) {
    Sentry.captureException(
      `OrderImageryModal rendered scene without a price. SourceId: ${sourceId}`,
      {
        extra: {
          orderableSceneSource: sourceId,
          ...(sceneSensingTime && {orderableSceneSensingTime: sceneSensingTime}),
        },
      }
    );

    return (
      <B.Callout icon={null} intent={B.Intent.DANGER}>
        <p>
          We’re sorry, we’re temporarily unable to process{' '}
          <strong>
            {sourceDetails.operator} {sourceDetails.name}
          </strong>{' '}
          data.
        </p>

        <p>
          Please check back soon or contact us at <strong>lens@upstream.tech</strong> with any
          questions.
        </p>
      </B.Callout>
    );
  }
  return (
    <B.Callout
      icon={null}
      intent={canPurchase ? B.Intent.WARNING : B.Intent.DANGER}
      style={{marginTop: 'auto'}}
    >
      <>
        <p>{acresMessage}</p>

        {orderGeometryAcres !== billableAcres && (
          <p>
            <strong>
              The minimum order amount for {sourceDetails.operator} {sourceDetails.name} is{' '}
              {formatCost(billableAcres * scenePricePerAcre)}.
            </strong>
          </p>
        )}
        {orderExceedsMaximumOrderAcres && (
          <p>
            <strong>
              This purchase exceeds the limit for automatic processing for {sourceDetails.operator}{' '}
              {sourceDetails.name}. Once you check out, our team will place the order on your behalf
              within 2 business days.
            </strong>
          </p>
        )}
        {isPreview ? (
          <NteExceededNotice
            nte={nte}
            orderCents={orderCents ?? calculateCost(billableAcres, scenePricePerAcre)}
            layerKey={layerKey}
            isPreview
          />
        ) : (
          <DollarUnitContractDetails
            usage={usage}
            nte={nte}
            acreage={billableAcres}
            layerKey={layerKey}
            pricePerAcre={scenePricePerAcre}
            orderCents={orderCents}
          />
        )}
      </>
    </B.Callout>
  );
};
